import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DeviceDetail, HistoryMetric, HistoryPoint } from '@irrigazione/shared';
import { api } from '../api/client';
import { Notice } from './ui';

/**
 * Ogni grafico e' a serie singola e porta il proprio titolo, quindi non serve
 * legenda e il colore non trasporta identita': si usa un solo colore validato
 * per tutti. Le tacche di soglia sono riferimenti, non dati, e restano in grigio.
 */
const RANGES = [
  { id: '24h', label: '24 ore', hours: 24 },
  { id: '7g', label: '7 giorni', hours: 24 * 7 },
  { id: '30g', label: '30 giorni', hours: 24 * 30 },
] as const;

export function HistoryTab({ device }: { device: DeviceDetail }) {
  const [rangeId, setRangeId] = useState<(typeof RANGES)[number]['id']>('24h');
  const [showTable, setShowTable] = useState(false);
  const range = RANGES.find((r) => r.id === rangeId)!;

  const to = new Date();
  const from = new Date(to.getTime() - range.hours * 3600_000);

  return (
    <div className="stack">
      <div className="row row-wrap" style={{ justifyContent: 'space-between' }}>
        <div className="row" role="group" aria-label="Intervallo">
          {RANGES.map((entry) => (
            <button
              key={entry.id}
              className={`btn btn--sm${entry.id === rangeId ? ' btn--primary' : ''}`}
              onClick={() => setRangeId(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </div>
        <button className="btn btn--sm" onClick={() => setShowTable((v) => !v)}>
          {showTable ? 'Mostra grafici' : 'Mostra tabella'}
        </button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
        <MetricPanel
          deviceId={device.id}
          metric="moisturePct"
          title="Umidità del terreno"
          subtitle="Percentuale calibrata"
          unit="%"
          domain={[0, 100]}
          threshold={device.config.moistureThresholdPct}
          thresholdLabel="soglia"
          from={from}
          to={to}
          showTable={showTable}
        />
        <MetricPanel
          deviceId={device.id}
          metric="batteryPct"
          title="Carica della batteria"
          subtitle="Percentuale stimata dalla curva LiPo"
          unit="%"
          domain={[0, 100]}
          from={from}
          to={to}
          showTable={showTable}
        />
        {device.type === 'standalone' && (
          <MetricPanel
            deviceId={device.id}
            metric="waterLevelPct"
            title="Livello del serbatoio"
            subtitle="Percentuale dell'altezza utile"
            unit="%"
            domain={[0, 100]}
            threshold={device.config.waterLevelMinPct}
            thresholdLabel="minimo"
            from={from}
            to={to}
            showTable={showTable}
          />
        )}
        <MetricPanel
          deviceId={device.id}
          metric="irrigationMinutes"
          title="Minuti di irrigazione"
          subtitle="Totale per giorno"
          unit=" min"
          from={from}
          to={to}
          showTable={showTable}
          asBars
        />
      </div>
    </div>
  );
}

interface PanelProps {
  deviceId: string;
  metric: HistoryMetric;
  title: string;
  subtitle: string;
  unit: string;
  domain?: [number, number];
  threshold?: number | null;
  thresholdLabel?: string;
  from: Date;
  to: Date;
  showTable: boolean;
  asBars?: boolean;
}

function MetricPanel(props: PanelProps) {
  const { deviceId, metric, from, to } = props;
  const query = useQuery({
    queryKey: ['history', deviceId, metric, from.toISOString().slice(0, 13), to.getTime() - from.getTime()],
    queryFn: () => api.history(deviceId, metric, from, to),
  });

  const points = query.data?.points ?? [];

  return (
    <section className="card chart-card">
      <h3>{props.title}</h3>
      <p className="tiny muted">{props.subtitle}</p>

      {query.error ? (
        <Notice variant="error">{(query.error as Error).message}</Notice>
      ) : query.isLoading ? (
        <div className="chart-empty">Caricamento…</div>
      ) : points.length === 0 ? (
        <div className="chart-empty">Nessun dato in questo intervallo</div>
      ) : props.showTable ? (
        <DataTable points={points} unit={props.unit} asBars={props.asBars} />
      ) : props.asBars ? (
        <BarsChart {...props} points={points} />
      ) : (
        <SeriesChart {...props} points={points} />
      )}
    </section>
  );
}

/** Asse temporale: nelle 24 ore l'ora basta, oltre serve la data. */
function tickFormatter(iso: string, spanHours: number): string {
  const date = new Date(iso);
  if (spanHours <= 26) {
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

function ChartTooltip({
  active,
  payload,
  label,
  unit,
  daily,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
  unit: string;
  daily?: boolean;
}) {
  if (!active || !payload?.length || label == null) return null;
  const date = new Date(label);
  const when = daily
    ? date.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' })
    : date.toLocaleString('it-IT', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
  return (
    <div className="chart-tooltip">
      <div className="tiny muted">{when}</div>
      <strong>
        {payload[0]!.value?.toFixed(1)}
        {unit}
      </strong>
    </div>
  );
}

const AXIS_STYLE = { fontSize: 11, fill: 'var(--text-muted)' };

function SeriesChart({ points, unit, domain, threshold, thresholdLabel, from, to }: PanelProps & { points: HistoryPoint[] }) {
  const spanHours = (to.getTime() - from.getTime()) / 3600_000;
  return (
    <ResponsiveContainer width="100%" height={210}>
      <LineChart data={points} margin={{ top: 6, right: 12, bottom: 0, left: -18 }}>
        <CartesianGrid stroke="var(--grid)" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="ts"
          tickFormatter={(value: string) => tickFormatter(value, spanHours)}
          tick={AXIS_STYLE}
          axisLine={{ stroke: 'var(--axis)' }}
          tickLine={false}
          minTickGap={28}
        />
        <YAxis
          domain={domain ?? ['auto', 'auto']}
          tick={AXIS_STYLE}
          axisLine={false}
          tickLine={false}
          width={44}
        />
        <Tooltip
          content={<ChartTooltip unit={unit} />}
          cursor={{ stroke: 'var(--axis)', strokeWidth: 1 }}
        />
        {threshold != null && (
          <ReferenceLine
            y={threshold}
            stroke="var(--text-muted)"
            strokeDasharray="4 4"
            label={{ value: thresholdLabel, position: 'insideTopRight', fontSize: 10, fill: 'var(--text-muted)' }}
          />
        )}
        <Line
          type="monotone"
          dataKey="value"
          stroke="var(--series-1)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          dot={false}
          // Il marker attivo porta l'anello nel colore della superficie, cosi'
          // resta leggibile dove incrocia la linea.
          activeDot={{ r: 4, fill: 'var(--series-1)', stroke: 'var(--surface-1)', strokeWidth: 2 }}
          isAnimationActive={false}
          connectNulls
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function BarsChart({ points, unit }: PanelProps & { points: HistoryPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={210}>
      <BarChart data={points} margin={{ top: 6, right: 12, bottom: 0, left: -18 }} barCategoryGap="18%">
        <CartesianGrid stroke="var(--grid)" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="ts"
          tickFormatter={(value: string) =>
            new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })
          }
          tick={AXIS_STYLE}
          axisLine={{ stroke: 'var(--axis)' }}
          tickLine={false}
          minTickGap={16}
        />
        <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} width={44} />
        <Tooltip
          content={<ChartTooltip unit={unit} daily />}
          cursor={{ fill: 'var(--series-1-wash)' }}
        />
        <Bar
          dataKey="value"
          fill="var(--series-1)"
          // Estremita' arrotondata sul dato, squadrata sulla linea di base.
          radius={[4, 4, 0, 0]}
          maxBarSize={24}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Vista tabellare: garantisce l'accesso ai valori senza dipendere dal colore. */
function DataTable({
  points,
  unit,
  asBars,
}: {
  points: HistoryPoint[];
  unit: string;
  asBars?: boolean;
}) {
  return (
    <div className="table-wrap" style={{ maxHeight: 210, overflowY: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>{asBars ? 'Giorno' : 'Istante'}</th>
            <th>Valore</th>
          </tr>
        </thead>
        <tbody>
          {[...points].reverse().map((point) => (
            <tr key={point.ts}>
              <td>
                {asBars
                  ? new Date(point.ts).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
                  : new Date(point.ts).toLocaleString('it-IT', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
              </td>
              <td className="num">
                {point.value == null ? '—' : `${point.value.toFixed(1)}${unit}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
